import { SafeHtmlComponent } from '@isdd/idsk-ui-kit/save-html-component/SafeHtmlComponent'
import { ConfigurationItemUi, RoleParticipantUI } from '@isdd/metais-common/api/generated/cmdb-swagger'
import { EnumType } from '@isdd/metais-common/api/generated/enums-repo-swagger'
import { Attribute, CiType } from '@isdd/metais-common/api/generated/types-repo-swagger'
import { setLangForAttribute } from '@isdd/metais-common/componentHelpers/englishAttributeLang'
import { DefinitionList } from '@isdd/metais-common/components/definition-list/DefinitionList'
import { DESCRIPTION, HTML_TYPE } from '@isdd/metais-common/constants'
import { QueryFeedback, pairEnumsToEnumValues, ATTRIBUTE_NAME } from '@isdd/metais-common/index'
import { Languages } from '@isdd/metais-common/localization/languages'
import { InformationGridRow } from '@isdd/metais-common/components/info-grid-row/InformationGridRow'
import { useGetCiTypeConstraintsData } from '@isdd/metais-common/hooks/useGetCiTypeConstraintsData'
import React from 'react'
import { useTranslation } from 'react-i18next'

interface CiInformationData {
    data: {
        ciItemData: ConfigurationItemUi | undefined
        ciTypeData: CiType | undefined
        constraintsData: (EnumType | undefined)[]
        unitsData?: EnumType | undefined
        gestorData: RoleParticipantUI[] | undefined
        roleParticipant?: RoleParticipantUI
    }
    isError: boolean
    isLoading: boolean
    additionalBasicInformation?: {
        top?: React.ReactNode
        bottom?: React.ReactNode
    }
    withoutDescription?: boolean
}

export const CiTrainingInformationView: React.FC<CiInformationData> = ({
    data: { ciItemData, ciTypeData, constraintsData, unitsData },
    isLoading,
    isError,
    additionalBasicInformation,
    withoutDescription = false,
}) => {
    const {
        t,
        i18n: { language },
    } = useTranslation()

    const {
        isLoading: isCiConstraintLoading,
        isError: isCiConstraintError,
        uuidsToMatchedCiItemsMap,
    } = useGetCiTypeConstraintsData(ciTypeData, [ciItemData ?? {}])

    const currentEntityCiTypeConstraintsData = uuidsToMatchedCiItemsMap[ciItemData?.uuid ?? '']

    const profileAttributes =
        ciTypeData?.attributeProfiles
            ?.filter((p) => p.valid)
            .reduce((accumulator: Attribute[], item) => {
                return [...accumulator, ...(item?.attributes ?? [])]
            }, []) ?? []
    const attributeList = [...(ciTypeData?.attributes ?? []), ...profileAttributes]

    return (
        <QueryFeedback
            loading={isLoading || isCiConstraintLoading}
            error={isError || isCiConstraintError}
            showSupportEmail
            errorProps={{ errorMessage: isCiConstraintError ? t('ciInformationAccordion.error') : undefined }}
            withChildren
        >
            <DefinitionList>
                {additionalBasicInformation?.top}
                {attributeList?.map((attribute) => {
                    const rowValue = pairEnumsToEnumValues({
                        attribute,
                        ciItemData,
                        constraintsData,
                        t,
                        unitsData,
                        matchedAttributeNamesToCiItem: currentEntityCiTypeConstraintsData,
                        withDescription: !withoutDescription,
                    })
                    const isHTML = attribute.type === HTML_TYPE || attribute.name == DESCRIPTION

                    if (attribute.technicalName === ATTRIBUTE_NAME.Gen_Profil_kod_metais) {
                        return
                    }

                    return (
                        <InformationGridRow
                            key={attribute?.technicalName}
                            label={(language === Languages.SLOVAK ? attribute.name : attribute.engName) ?? ''}
                            value={isHTML ? <SafeHtmlComponent dirtyHtml={(rowValue as string)?.replace(/\n/g, '<br>')} /> : rowValue}
                            tooltip={attribute?.description}
                            lang={setLangForAttribute({ language, technicalName: attribute.technicalName ?? '' })}
                        />
                    )
                })}
                {additionalBasicInformation?.bottom}
            </DefinitionList>
        </QueryFeedback>
    )
}
